import axios from 'axios';
import type { ApiErrorResponse } from '@amuster/types';

// ── CSRF Token Management (Platform Admin) ──────────────────────────────────
// In-memory CSRF token storage (never persisted to localStorage)
let paCsrfToken: string | null = null;

export function setPaCsrfToken(token: string): void {
  paCsrfToken = token;
}

export function getPaCsrfToken(): string | null {
  return paCsrfToken;
}

// Methods that require CSRF protection
const CSRF_REQUIRED_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

// ── Axios Instance ──────────────────────────────────────────────────────────
// Separate axios instance for platform admin to avoid tenant interceptors
const platformAdminApi = axios.create({
  baseURL: '/api/platform-admin',
  headers: {
    'Content-Type': 'application/json',
    'X-Use-Cookies': 'true', // Signal to API that we want cookie-based auth
  },
  withCredentials: true, // Send cookies with every request
});

// ── Request Interceptor ─────────────────────────────────────────────────────
platformAdminApi.interceptors.request.use((requestConfig) => {
  // Inject CSRF token for state-changing requests
  const method = (requestConfig.method ?? 'GET').toUpperCase();
  if (CSRF_REQUIRED_METHODS.includes(method) && paCsrfToken) {
    requestConfig.headers['X-CSRF-Token'] = paCsrfToken;
  }
  return requestConfig;
});

// ── Response Interceptor ────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}> = [];

/** Max time (ms) a queued request waits for an in-flight refresh to resolve. */
const REFRESH_QUEUE_TIMEOUT_MS = 10_000;

/**
 * Drain the entire queue — either resolving all waiters (on success)
 * or rejecting them (on failure / timeout). Resets isRefreshing so
 * subsequent 401s start a fresh refresh attempt.
 */
function processQueue(error: unknown): void {
  const queue = failedQueue;
  failedQueue = [];
  isRefreshing = false;
  queue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(undefined);
    }
  });
}

platformAdminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - attempt cookie-based refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/me')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          const entry = { resolve, reject };
          const timer = setTimeout(() => {
            processQueue(new Error('Session refresh timed out'));
          }, REFRESH_QUEUE_TIMEOUT_MS);
          entry.resolve = (v: unknown) => { clearTimeout(timer); resolve(v); };
          entry.reject = (e: unknown) => { clearTimeout(timer); reject(e); };
          failedQueue.push(entry);
        }).then(() => platformAdminApi(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Cookie-based refresh: server reads refresh_token from HttpOnly cookie
        await axios.post('/api/platform-admin/auth/refresh', {}, {
          withCredentials: true,
          headers: { 'X-Use-Cookies': 'true' },
        });

        processQueue(null);
        return platformAdminApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Redirect to platform admin login on refresh failure
        window.location.href = '/platform-admin/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export function getPlatformAdminErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    if (data?.error?.message) {
      return data.error.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

export default platformAdminApi;
